import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { fileUrl } from "../lib/api";
import {
  MapPin,
  CaretLeft,
  CaretRight,
  X,
  Sparkle,
} from "@phosphor-icons/react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import { makeRepertoPin } from "./SwissTopoMap";
import "leaflet/dist/leaflet.css";

const Row = ({ label, value }) => (
  <div className="border-b border-black/5 py-3">
    <div className="label-eyebrow mb-1">{label}</div>
    <div className="text-[#1E2520]">
      {value || <span className="text-[#5C635A] italic">non specificato</span>}
    </div>
  </div>
);

const OSM_TILE = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

const MiniMap = ({ location, code }) => {
  const icon = useMemo(() => makeRepertoPin(code || "?"), [code]);
  if (!location?.lat) return null;
  return (
    <div className="card overflow-hidden">
      <MapContainer
        center={[location.lat, location.lng]}
        zoom={14}
        scrollWheelZoom={false}
        style={{ height: 320, width: "100%" }}
        attributionControl={true}
      >
        <TileLayer
          url={OSM_TILE}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          maxZoom={19}
        />
        <Marker position={[location.lat, location.lng]} icon={icon} />
      </MapContainer>
    </div>
  );
};

/**
 * Read-only artifact detail body used by both single-share (`/share/:token`)
 * and guest-catalog modal (`/share/catalog/:token`). Renders hero + thumbs +
 * metadata grid + description + anecdote + optional mini-map + fullscreen
 * lightbox. NO page chrome (header/footer) — callers wrap it.
 */
export default function ArtifactPublicDetail({ item }) {
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox || !item?.photos) return;
    const handler = (e) => {
      const photos = item.photos || [];
      const idx = photos.indexOf(lightbox);
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight" && idx < photos.length - 1)
        setLightbox(photos[idx + 1]);
      if (e.key === "ArrowLeft" && idx > 0) setLightbox(photos[idx - 1]);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, item]);

  if (!item) return null;
  const ident = item.identification || {};
  const photos = item.photos || [];
  const mainPhoto = photos[activePhoto] || photos[0];
  const hasLocation = !!item.location?.lat;

  return (
    <>
      {/* Hero: big photo + thumbnails */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-10">
        <div className="md:col-span-3">
          {mainPhoto ? (
            <button
              type="button"
              onClick={() => setLightbox(mainPhoto)}
              className="block w-full aspect-[4/3] overflow-hidden bg-[#E5E0D8] border border-black/5 group"
              data-testid="public-hero-photo"
            >
              <img
                src={fileUrl(mainPhoto)}
                alt={ident.nome || "Reperto"}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
              />
            </button>
          ) : (
            <div className="w-full aspect-[4/3] bg-[#E5E0D8] flex items-center justify-center text-[#5C635A]">
              Nessuna foto
            </div>
          )}
          {photos.length > 1 && (
            <div
              className="grid grid-cols-6 gap-2 mt-3"
              data-testid="public-thumbnails"
            >
              {photos.map((p, i) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setActivePhoto(i)}
                  onDoubleClick={() => setLightbox(p)}
                  className={`aspect-square overflow-hidden bg-[#E5E0D8] border transition-all ${
                    i === activePhoto
                      ? "border-[#9C5B42] ring-2 ring-[#9C5B42]/30"
                      : "border-black/10 hover:border-[#9C5B42]/50"
                  }`}
                  data-testid={`public-thumb-${i}`}
                  aria-label={`Foto ${i + 1}`}
                >
                  <img
                    src={fileUrl(p)}
                    alt={`Foto ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Title + value */}
        <div className="md:col-span-2 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              {item.inventory_code && (
                <span
                  className="inline-block bg-[#1E2520] text-[#F9F8F6] font-mono text-xs tracking-[0.18em] px-2.5 py-1 rounded-sm"
                  data-testid="public-inventory-code"
                >
                  {item.inventory_code}
                </span>
              )}
              <div className="label-eyebrow">
                {ident.categoria}
                {ident.periodo ? ` · ${ident.periodo}` : ""}
              </div>
            </div>
            <h1
              className="font-serif text-3xl md:text-4xl tracking-tight text-[#1E2520] mb-2"
              data-testid="public-title"
            >
              {ident.nome || "Reperto"}
            </h1>
            {ident.categoria_dettaglio &&
              ident.categoria_dettaglio !== ident.categoria && (
                <div className="text-[#5C635A] italic mb-3">
                  {ident.categoria_dettaglio}
                </div>
              )}
            {ident.confidenza && (
              <div className="inline-flex items-center gap-1.5 text-xs text-[#5C635A] bg-[#F0EBE1]/60 px-2 py-1 rounded-sm mb-4">
                <Sparkle weight="duotone" size={12} className="text-[#9C5B42]" />
                Confidenza AI:{" "}
                <span className="capitalize font-medium text-[#1E2520]">
                  {ident.confidenza}
                </span>
              </div>
            )}
          </div>

          <div className="card p-5 md:p-6 mt-4" data-testid="public-value-card">
            <div className="label-eyebrow mb-1">Valore stimato</div>
            <div className="font-serif text-4xl text-[#9C5B42] leading-none">
              € {(ident.valore_stimato_eur ?? 0).toLocaleString("it-IT")}
            </div>
            {ident.valore_min_eur != null && ident.valore_max_eur != null && (
              <div className="text-xs text-[#5C635A] mt-2">
                Range: € {ident.valore_min_eur?.toLocaleString("it-IT")} – €{" "}
                {ident.valore_max_eur?.toLocaleString("it-IT")}
              </div>
            )}
            {ident.note_valore && (
              <div className="text-xs text-[#5C635A] mt-3 italic border-t border-black/5 pt-3">
                {ident.note_valore}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Technical grid */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mb-10"
        data-testid="public-details-grid"
      >
        <Row label="Categoria" value={ident.categoria} />
        <Row label="Datazione" value={ident.datazione} />
        <Row label="Periodo" value={ident.periodo} />
        <Row label="Materiale" value={ident.materiale} />
        <Row label="Cultura" value={ident.cultura} />
        <Row label="Dimensioni" value={ident.dimensioni_stimate} />
        <Row label="Stato di conservazione" value={ident.stato_conservazione} />
        <Row label="Rarità" value={ident.rarita} />
      </div>

      {/* Description */}
      <div className="card p-6 md:p-8 mb-8">
        <div className="label-eyebrow mb-3">Descrizione storica</div>
        <p
          className="text-[#1E2520] leading-relaxed font-serif text-lg whitespace-pre-wrap"
          data-testid="public-description"
        >
          {ident.descrizione || (
            <span className="text-[#5C635A] italic">Nessuna descrizione.</span>
          )}
        </p>
      </div>

      {/* Anecdote */}
      {ident.aneddoto_storico && (
        <div className="card p-6 md:p-8 mb-8 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -top-6 -right-6 font-serif text-[180px] leading-none text-[#9C5B42]/10 select-none pointer-events-none"
          >
            ❝
          </div>
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <Sparkle weight="duotone" size={14} className="text-[#9C5B42]" />{" "}
            Aneddoto storico
          </div>
          <p
            className="text-[#1E2520] leading-relaxed font-serif text-lg italic relative z-10 whitespace-pre-wrap"
            data-testid="public-anecdote"
          >
            {ident.aneddoto_storico}
          </p>
        </div>
      )}

      {/* Location (only present when backend allowed it — i.e. flag ON) */}
      {hasLocation && (
        <div className="card p-6 md:p-8 mb-8" data-testid="public-location">
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <MapPin weight="duotone" size={14} className="text-[#9C5B42]" />{" "}
            Luogo del ritrovamento
          </div>
          {item.location.label && (
            <div className="text-[#1E2520] mb-3 font-serif text-lg">
              {item.location.label}
            </div>
          )}
          <MiniMap location={item.location} code={item.inventory_code} />
          <div className="text-xs text-[#5C635A] font-mono mt-2">
            {item.location.lat.toFixed(5)}, {item.location.lng.toFixed(5)}
            {item.location.alt != null &&
              ` · ${Math.round(item.location.alt)} m s.l.m.`}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
            onClick={() => setLightbox(null)}
            style={{ touchAction: "pinch-zoom" }}
            data-testid="public-lightbox"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(null);
              }}
              className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
              aria-label="Chiudi"
              data-testid="lightbox-close"
            >
              <X size={20} />
            </button>

            {photos.length > 1 && photos.indexOf(lightbox) > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const idx = photos.indexOf(lightbox);
                  setLightbox(photos[idx - 1]);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
                aria-label="Foto precedente"
                data-testid="lightbox-prev"
              >
                <CaretLeft size={22} />
              </button>
            )}
            {photos.length > 1 &&
              photos.indexOf(lightbox) < photos.length - 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const idx = photos.indexOf(lightbox);
                    setLightbox(photos[idx + 1]);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full"
                  aria-label="Foto successiva"
                  data-testid="lightbox-next"
                >
                  <CaretRight size={22} />
                </button>
              )}

            <img
              key={lightbox}
              src={fileUrl(lightbox)}
              alt="Foto reperto ingrandita"
              className="block max-h-[90vh] max-w-[96vw] w-auto h-auto object-contain select-none"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
